import React, { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Page } from "./components/Layout/Page/Page";
import { PatchRadixForWebView } from "./lib/radix-webview-fix";

import { Wifi, WifiOff } from "lucide-react";

import { useStartup } from "./hooks/useStartup";

import { AccessibilityTestPage } from "./components/eds/AccessibilityTestPage";
import { PageLoader } from "./components/Layout/PageLoader/PageLoader";
import { useNotificationsContext } from "./hooks/useNotificationsContext";
import { ToastOptions } from "./types/toast";

import "./app.scss";
import { ErrorBoundary } from "./components/Pages/Error/ErrorBoundary";
import ErrorPage from "./components/Pages/Error/ErrorPage";
import {
  reportError,
  resetAfterError
} from "./components/Pages/Error/ErrorPageController";
import { useDeviceContext } from "./context/DeviceContext";
import { isBrowser } from "./utils/environment";
import { getColourScheme } from "./utils/nativeUtils/customActions";

import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "./hooks/useAppContext";
import { useExternalLinkInterceptor } from "./hooks/useExternalLinkInterceptor";
import { useIntercom } from "./hooks/useIntercom";
import useManageAPIVenueData from "./hooks/useManageAPIVenueData";
import useManageOrgData from "./hooks/useManageOrgData";
import { useTelemetry } from "./hooks/useTelemetry";
import { useUserContext } from "./hooks/useUserContext";
import { HomePage } from "./pages/dashboard";
import { DebugPage } from "./pages/debug";
import { DiaryPage } from "./pages/diary";
import { EnquiriesPage } from "./pages/enquiries";
import { SettingsPage } from "./pages/settings";
import { SoonPage } from "./pages/soon";
import { StoragePage } from "./pages/storage";

declare global {
  interface Window {
    locale: string;
  }
}
function App() {
  // Initialise authentication and user context
  const { status } = useStartup();
  // React Router setup
  const location = useLocation();
  // Toast notification context
  const notificationsContext = useNotificationsContext();

  // Organisation data management
  const { loadOrganisationData } = useManageOrgData();

  // App API Data
  useManageAPIVenueData();

  const { connectionStatus, setTheme } = useDeviceContext();

  // User and organisation context for Intercom
  const { user } = useUserContext();
  const { venueGroupConfig } = useAppContext();

  // Initialise Intercom widget (Collins Digital Assistant)
  useIntercom({
    user,
    venueGroupConfig,
    isAuthenticated: status === "success"
  });

  // Intercept clicks on external links (e.g. from Intercom or other
  // third-party widgets) so they open in the device browser rather
  // than navigating the webview.
  useExternalLinkInterceptor();

  // CRITICAL: Apply theme immediately on page load to prevent race condition
  React.useEffect(() => {
    const applyThemeImmediately = async () => {
      try {
        const storageTheme = await getColourScheme();

        // Apply theme to DOM immediately
        document.body.setAttribute("data-theme", storageTheme);
        if (storageTheme === "Dark") {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      } catch (error) {
        console.error("Failed to apply theme immediately:", error);
      }
    };
    applyThemeImmediately();
  }, []);

  useTelemetry();

  // Load organisation data after startup is complete
  useQuery({
    queryKey: ["organisationData", status],
    queryFn: loadOrganisationData,
    enabled: status === "success",
    gcTime: 0,
    refetchInterval: 17 * 60 * 1000, // 17 minute interval to not interfere with venue config reload
    refetchIntervalInBackground: false // Optional: don't refetch when tab is not active
  });

  const initTheme = async () => {
    // Check local storage for previously set theme - Added when changed in settings toggle
    const storageTheme = await getColourScheme();
    // Apply theme
    const currentTheme = storageTheme;
    setTheme(currentTheme);
  };

  const configMode = async () => {
    // Update for CSS styling based on browser or mobile
    const mode = isBrowser() ? "browser" : "mobile";
    document.body.setAttribute("data-mode", mode);
  };

  useEffect(() => {
    // Initialize theme immediately to prevent race condition on reload
    const initializeTheme = async () => {
      await initTheme();
      configMode();
    };
    initializeTheme();
  }, []);

  useEffect(() => {
    if (connectionStatus === "unknown") {
      return;
    }

    // Toast notification for connection status change
    const messageMap: any = {
      internet: (
        <div>
          Connection established <Wifi />
        </div>
      ),
      none: (
        <div>
          Working offline <WifiOff />
        </div>
      ),
      local: (
        <div>
          Limited connectivity <Wifi />
        </div>
      ),
      constrainedInternet: (
        <div>
          Slow connection <Wifi />
        </div>
      )
    };

    notificationsContext.addNotification({
      heading: messageMap[connectionStatus],
      autoClose: 2000,
      toastType: "information"
    });
  }, [connectionStatus]);

  window.addEventListener("toastNotification", ((
    e: CustomEvent<ToastOptions>
  ) => {
    notificationsContext.addNotification(e.detail);
  }) as unknown as EventListener);

  return (
    // Catch any JSX render errors and display an error page
    <ErrorBoundary
      FallbackComponent={ErrorPage}
      onError={reportError}
      onReset={resetAfterError}
    >
      <>
        {/* Global patch for Radix UI positioning in WebView */}
        <PatchRadixForWebView />
        <div className={`App App--load-${status}`}>
          <Suspense fallback={<PageLoader />}>
            <Routes location={location}>
              <Route
                path="/bookings/:bookingId"
                element={
                  <Page title="Booking Details">
                    <DiaryPage />
                  </Page>
                }
              />
              <Route
                path="/sites/:venueId/:tab"
                element={
                  <Page title="Venue Diary">
                    <DiaryPage />
                  </Page>
                }
              />
              <Route
                path="/enquiries"
                element={
                  <Page title="Enquiries">
                    <EnquiriesPage />
                  </Page>
                }
              />
              <Route
                path="/diary"
                element={
                  <Page title="Diary">
                    <DiaryPage />
                  </Page>
                }
              />
              <Route
                path="/storage"
                element={
                  <Page title="Storage">
                    <StoragePage />
                  </Page>
                }
              />
              <Route
                path="/debug"
                element={
                  <Page title="Debug">
                    <DebugPage />
                  </Page>
                }
              />
              <Route
                path="/settings"
                element={
                  <Page title="Settings">
                    <SettingsPage />
                  </Page>
                }
              />
              <Route
                path="/accessibility-test"
                element={
                  <Page title="Accessibility Test">
                    <AccessibilityTestPage />
                  </Page>
                }
              />
              <Route
                path="/soon"
                element={
                  <Page title="Coming Soon">
                    <SoonPage />
                  </Page>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <Page title="Dashboard">
                    <HomePage />
                  </Page>
                }
              />
              <Route path="/" element={<Navigate to="/diary" replace />} />
              <Route
                path="*"
                element={
                  <Page title="Coming Soon">
                    <SoonPage />
                  </Page>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </>
    </ErrorBoundary>
  );
}

export default App;
